import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { TabBar } from '../components/TabBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SignatureModal from '../components/SignatureModal';
import { UnitTab } from '../components/POEUnit';
import { AttendancePanel } from '../components/AttendancePanel';
import { GradingStudentSelector } from '../components/GradingStudentSelector';
import { toast } from 'sonner';
import {
    ArrowLeft, Users, UserPlus, UserMinus, ClipboardCheck, Award, Search, Loader2,
    Check, X, Save, UsersRound, Trash2, Lock, ChevronRight
} from 'lucide-react';

const POEUnitDetailPage = () => {
    const { unitId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeaders, API } = useAuth();

    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('students');
    const [template, setTemplate] = useState(null);
    const [unit, setUnit] = useState(null);
    const [enrolled, setEnrolled] = useState([]);
    const [allStudents, setAllStudents] = useState([]);
    const [groups, setGroups] = useState([]);
    const [predefinedComments, setPredefinedComments] = useState([]);
    const [groupFilter, setGroupFilter] = useState('all');

    // Modals
    const [enrollOpen, setEnrollOpen] = useState(false);
    const [enrollSearch, setEnrollSearch] = useState('');
    const [groupOpen, setGroupOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');

    // Attendance
    const [att, setAtt] = useState({});
    const [attSaving, setAttSaving] = useState(false);
    const [attSearch, setAttSearch] = useState('');

    // Grading
    const [gradeStudentId, setGradeStudentId] = useState('');
    const [grades, setGrades] = useState({});
    const [unitMeta, setUnitMeta] = useState({});
    const [locked, setLocked] = useState(false);
    const [loadingGrade, setLoadingGrade] = useState(false);
    const [sigModal, setSigModal] = useState({ open: false, handler: null, title: '' });

    const fetchAll = useCallback(async () => {
        try {
            const [tplRes, enrRes, allRes, grpRes, attRes, comRes] = await Promise.all([
                axios.get(`${API}/poe/template`, { headers: getAuthHeaders() }),
                axios.get(`${API}/poe/enrolled-students`, { headers: getAuthHeaders() }),
                axios.get(`${API}/students`, { headers: getAuthHeaders() }),
                axios.get(`${API}/poe/groups`, { headers: getAuthHeaders() }),
                axios.get(`${API}/poe/unit/${unitId}/attendance`, { headers: getAuthHeaders() }),
                axios.get(`${API}/admin/attendance-comments`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
            ]);
            setTemplate(tplRes.data);
            setUnit((tplRes.data.units || []).find(u => u.id === unitId) || null);
            setEnrolled(enrRes.data);
            setAllStudents(allRes.data);
            setGroups(grpRes.data);
            setPredefinedComments(comRes.data || []);
            const m = {};
            attRes.data.forEach(a => { m[a.student_id] = { present: a.present, comment: a.comment || '', predefined_comments: a.predefined_comments || [] }; });
            setAtt(m);
        } catch (e) {
            toast.error('Failed to load POE unit');
        } finally {
            setLoading(false);
        }
    }, [API, unitId, getAuthHeaders]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const sortBySurname = (list) => [...list].sort((a, b) => {
        if ((a.year || 0) !== (b.year || 0)) return (a.year || 0) - (b.year || 0);
        return (a.name || '').split(' ').pop().toLowerCase().localeCompare((b.name || '').split(' ').pop().toLowerCase());
    });

    const groupOf = (sid) => groups.find(g => (g.student_ids || []).includes(sid));

    const filteredEnrolled = sortBySurname(enrolled.filter(s => {
        if (groupFilter === 'all') return true;
        if (groupFilter === 'ungrouped') return !groupOf(s.id);
        return (groups.find(g => g.id === groupFilter)?.student_ids || []).includes(s.id);
    }));

    // ---- Enrollment ----
    const enroll = async (ids) => {
        try {
            await axios.post(`${API}/poe/assign`, { student_ids: ids }, { headers: getAuthHeaders() });
            toast.success(`Enrolled ${ids.length} student(s)`);
            const enrRes = await axios.get(`${API}/poe/enrolled-students`, { headers: getAuthHeaders() });
            setEnrolled(enrRes.data);
        } catch (e) { toast.error('Failed to enroll'); }
    };

    const unenroll = async (sid) => {
        if (!window.confirm('Remove this student from the POE? Their existing grades are kept.')) return;
        try {
            await axios.delete(`${API}/poe/student/${sid}/assignment`, { headers: getAuthHeaders() });
            toast.success('Student removed');
            setEnrolled(prev => prev.filter(s => s.id !== sid));
        } catch (e) { toast.error('Failed to remove'); }
    };

    // ---- Groups ----
    const createGroup = async () => {
        if (!newGroupName.trim()) return;
        try {
            await axios.post(`${API}/poe/groups`, { name: newGroupName.trim() }, { headers: getAuthHeaders() });
            setNewGroupName('');
            const grpRes = await axios.get(`${API}/poe/groups`, { headers: getAuthHeaders() });
            setGroups(grpRes.data);
            toast.success('Group created');
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create group'); }
    };

    const deleteGroup = async (gid) => {
        try {
            await axios.delete(`${API}/poe/groups/${gid}`, { headers: getAuthHeaders() });
            const grpRes = await axios.get(`${API}/poe/groups`, { headers: getAuthHeaders() });
            setGroups(grpRes.data);
            toast.success('Group deleted');
        } catch (e) { toast.error('Failed to delete group'); }
    };

    const assignGroup = async (sid, gid) => {
        try {
            if (gid === 'none') {
                const cur = groupOf(sid);
                if (cur) await axios.delete(`${API}/poe/groups/${cur.id}/remove-student/${sid}`, { headers: getAuthHeaders() });
            } else {
                await axios.post(`${API}/poe/groups/${gid}/add-student/${sid}`, {}, { headers: getAuthHeaders() });
            }
            const grpRes = await axios.get(`${API}/poe/groups`, { headers: getAuthHeaders() });
            setGroups(grpRes.data);
        } catch (e) { toast.error('Failed to update group'); }
    };

    // ---- Attendance ----
    const setAttField = (sid, field, value) => setAtt(prev => ({ ...prev, [sid]: { ...(prev[sid] || { present: false, comment: '', predefined_comments: [] }), [field]: value } }));
    const togglePredefined = (sid, text) => {
        setAtt(prev => {
            const cur = prev[sid] || { present: false, comment: '', predefined_comments: [] };
            const list = cur.predefined_comments || [];
            const next = list.includes(text) ? list.filter(t => t !== text) : [...list, text];
            return { ...prev, [sid]: { ...cur, predefined_comments: next } };
        });
    };
    const saveAttendance = async () => {
        setAttSaving(true);
        try {
            const records = enrolled.map(s => ({
                student_id: s.id,
                present: att[s.id]?.present ?? false,
                comment: att[s.id]?.comment || '',
                predefined_comments: att[s.id]?.predefined_comments || [],
            }));
            await axios.post(`${API}/poe/unit/${unitId}/attendance`, { records }, { headers: getAuthHeaders() });
            toast.success('Attendance saved');
        } catch (e) { toast.error('Failed to save attendance'); }
        finally { setAttSaving(false); }
    };

    // ---- Grading ----
    const loadGradeStudent = async (sid) => {
        setGradeStudentId(sid);
        setLoadingGrade(true);
        try {
            const res = await axios.get(`${API}/poe/student/${sid}`, { headers: getAuthHeaders() });
            const a = res.data.assignment || {};
            setGrades(a.grades || {});
            setUnitMeta((a.units || {})[unitId] || {});
            setLocked(a.locked || false);
        } catch (e) { toast.error('Failed to load student grades'); }
        finally { setLoadingGrade(false); }
    };

    const saveGrade = async (outcomeId, score, date) => {
        if (locked || !gradeStudentId) return;
        const next = { ...grades };
        if (score === null || score === undefined) delete next[outcomeId];
        else next[outcomeId] = { ...(next[outcomeId] || {}), score, date: date ?? next[outcomeId]?.date ?? '' };
        setGrades(next);
        try {
            await axios.post(`${API}/poe/student/${gradeStudentId}/grade`, { outcome_id: outcomeId, score, date: next[outcomeId]?.date || '' }, { headers: getAuthHeaders() });
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    };

    const saveUnitMeta = async (uId, fields) => {
        if (locked || !gradeStudentId) return;
        setUnitMeta(prev => ({ ...prev, ...fields }));
        try {
            await axios.post(`${API}/poe/student/${gradeStudentId}/unit-meta`, { unit_id: uId, ...fields }, { headers: getAuthHeaders() });
        } catch (e) { toast.error('Failed to save'); }
    };

    const openSig = (title, handler) => setSigModal({ open: true, title, handler });

    if (loading) return <Layout><div className="p-10 text-center text-gray-400">Loading...</div></Layout>;
    if (!unit) return <Layout><div className="p-10 text-center text-gray-400">Unit not found</div></Layout>;

    const candidates = allStudents.filter(s => !enrolled.some(e => e.id === s.id)).filter(s => {
        const q = enrollSearch.toLowerCase();
        return s.name.toLowerCase().includes(q) || s.student_id.toLowerCase().includes(q);
    });

    return (
        <Layout>
            <div className="space-y-5 animate-fade-in" data-testid="poe-unit-detail-page">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/lessons')} data-testid="poe-unit-back-btn">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Lessons
                    </Button>
                    <div>
                        <div className="flex items-center gap-2">
                            {unit.evidence_reference && <span className="text-xs font-mono px-2 py-0.5 rounded bg-ica-navy/10 text-ica-navy dark:text-ica-light-gold">{unit.evidence_reference}</span>}
                            <h1 className="font-heading text-2xl font-bold text-ica-navy">{unit.unit_no}: {unit.unit_name}</h1>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-slate-400 flex items-center gap-1 mt-0.5"><Users className="w-3.5 h-3.5" /> {enrolled.length} enrolled</p>
                    </div>
                </div>

                {/* Tabs */}
                <TabBar
                    tabs={[{ id: 'students', label: 'Students', icon: Users }, { id: 'attendance', label: 'Attendance', icon: ClipboardCheck }, { id: 'grading', label: 'Grading', icon: Award }]}
                    active={activeTab} onChange={setActiveTab} testIdPrefix="poe-unit-tab" />

                {/* ---- STUDENTS ---- */}
                {activeTab === 'students' && (
                    <div className="space-y-4">
                        <div className="flex gap-2">
                            <Button onClick={() => setEnrollOpen(true)} data-testid="poe-add-students-btn">
                                <UserPlus className="w-4 h-4 mr-2" /> Add Students
                            </Button>
                            <Button variant="outline" onClick={() => setGroupOpen(true)} data-testid="poe-manage-groups-btn">
                                <UsersRound className="w-4 h-4 mr-2" /> Manage Groups
                            </Button>
                        </div>

                        {/* Group filter */}
                        <div className="flex flex-wrap gap-2">
                            <button onClick={() => setGroupFilter('all')} className={`px-3 py-1 rounded-full text-xs font-medium ${groupFilter === 'all' ? 'bg-ica-navy text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'}`}>All ({enrolled.length})</button>
                            {groups.map(g => (
                                <button key={g.id} onClick={() => setGroupFilter(g.id)} className={`px-3 py-1 rounded-full text-xs font-medium ${groupFilter === g.id ? 'bg-ica-navy text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'}`}>{g.name} ({(g.student_ids || []).length})</button>
                            ))}
                            <button onClick={() => setGroupFilter('ungrouped')} className={`px-3 py-1 rounded-full text-xs font-medium ${groupFilter === 'ungrouped' ? 'bg-ica-navy text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'}`}>Ungrouped</button>
                        </div>

                        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                            {filteredEnrolled.length === 0 ? (
                                <div className="p-10 text-center text-gray-400">No students enrolled. Click "Add Students".</div>
                            ) : (
                                <div className="divide-y divide-gray-50 dark:divide-slate-700">
                                    {filteredEnrolled.map(s => (
                                        <div key={s.id} className="flex items-center px-5 py-3 gap-3" data-testid={`poe-enrolled-${s.id}`}>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 dark:text-slate-100 truncate">{s.name}</p>
                                                <p className="text-xs text-gray-500 dark:text-slate-400 font-mono">{s.student_id} · Year {s.year}</p>
                                            </div>
                                            <Select value={groupOf(s.id)?.id || 'none'} onValueChange={(v) => assignGroup(s.id, v)}>
                                                <SelectTrigger className="w-40 h-8 text-xs" data-testid={`poe-group-select-${s.id}`}><SelectValue placeholder="No group" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">No group</SelectItem>
                                                    {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                            <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-ica-navy" onClick={() => { setActiveTab('grading'); loadGradeStudent(s.id); }} title="Grade" data-testid={`poe-grade-link-${s.id}`}>
                                                <Award className="w-4 h-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm" className="h-8 px-2 text-red-400 hover:text-red-600" onClick={() => unenroll(s.id)} title="Remove" data-testid={`poe-unenroll-${s.id}`}>
                                                <UserMinus className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ---- ATTENDANCE ---- */}
                {activeTab === 'attendance' && (
                    <AttendancePanel
                        students={sortBySurname(enrolled)}
                        groups={groups}
                        groupFilter={groupFilter}
                        onGroupFilter={setGroupFilter}
                        search={attSearch}
                        onSearch={setAttSearch}
                        predefinedComments={predefinedComments}
                        isPresent={(sid) => att[sid]?.present}
                        onSetPresent={(sid, val) => setAttField(sid, 'present', val)}
                        predefinedFor={(sid) => att[sid]?.predefined_comments || []}
                        onTogglePredefined={(sid, label) => togglePredefined(sid, label)}
                        commentFor={(sid) => att[sid]?.comment || ''}
                        onSetComment={(sid, val) => setAttField(sid, 'comment', val)}
                        onSave={saveAttendance}
                        saving={attSaving}
                        saveTestId="poe-save-attendance-btn"
                    />
                )}

                {/* ---- GRADING ---- */}
                {activeTab === 'grading' && (
                    <div className="space-y-4">
                        {enrolled.length === 0 ? (
                            <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-12 text-center">
                                <Award className="w-14 h-14 mx-auto mb-4 text-gray-300" />
                                <p className="text-gray-500 dark:text-slate-400 text-sm">No students enrolled yet — add students in the Students tab first.</p>
                            </div>
                        ) : (
                            <>
                                <GradingStudentSelector
                                    students={sortBySurname(enrolled)}
                                    groups={groups}
                                    groupFilter={groupFilter}
                                    onGroupFilter={setGroupFilter}
                                    currentStudentId={gradeStudentId}
                                    onSelectStudent={loadGradeStudent}
                                    lockedStudentIds={enrolled.filter(s => s.locked).map(s => s.id)}
                                    statusSlot={locked && gradeStudentId ? (
                                        <div className="flex justify-end"><span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1"><Lock className="w-3 h-3" /> This student's POE is locked</span></div>
                                    ) : null}
                                />
                                {!gradeStudentId ? (
                                    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-12 text-center">
                                        <Award className="w-14 h-14 mx-auto mb-4 text-gray-300" />
                                        <p className="text-gray-500 dark:text-slate-400 text-sm">Select a student above to grade this unit. Grades feed directly into their Portfolio of Evidence.</p>
                                    </div>
                                ) : loadingGrade ? (
                                    <div className="p-10 text-center text-gray-400">Loading...</div>
                                ) : (
                                    <UnitTab unit={unit} template={template} grades={grades} unitMeta={unitMeta} locked={locked} saveGrade={saveGrade} saveUnitMeta={saveUnitMeta} openSig={openSig} />
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Enroll Modal */}
                <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
                    <DialogContent data-testid="poe-enroll-modal">
                        <DialogHeader><DialogTitle>Add Students to POE</DialogTitle></DialogHeader>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input value={enrollSearch} onChange={(e) => setEnrollSearch(e.target.value)} placeholder="Search students..." className="pl-10" data-testid="poe-enroll-search" />
                        </div>
                        <div className="max-h-80 overflow-y-auto divide-y divide-gray-50 dark:divide-slate-700">
                            {candidates.length === 0 ? (
                                <p className="text-sm text-gray-400 py-6 text-center">All students are already enrolled</p>
                            ) : candidates.map(s => (
                                <div key={s.id} className="flex items-center justify-between py-2">
                                    <div>
                                        <p className="text-sm font-medium text-gray-800 dark:text-slate-100">{s.name}</p>
                                        <p className="text-xs text-gray-400 font-mono">{s.student_id} · Year {s.year}</p>
                                    </div>
                                    <Button size="sm" variant="outline" onClick={() => enroll([s.id])} data-testid={`poe-enroll-add-${s.id}`}><UserPlus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                                </div>
                            ))}
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Groups Modal */}
                <Dialog open={groupOpen} onOpenChange={setGroupOpen}>
                    <DialogContent data-testid="poe-groups-modal">
                        <DialogHeader><DialogTitle>Manage POE Groups</DialogTitle></DialogHeader>
                        <div className="flex gap-2">
                            <Input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="New group name" data-testid="poe-new-group-input" />
                            <Button onClick={createGroup} data-testid="poe-create-group-btn">Create</Button>
                        </div>
                        <div className="space-y-2 mt-2">
                            {groups.length === 0 ? <p className="text-sm text-gray-400">No groups yet.</p> : groups.map(g => (
                                <div key={g.id} className="flex items-center justify-between p-2 rounded-lg border border-gray-100 dark:border-slate-700">
                                    <span className="text-sm font-medium text-gray-800 dark:text-slate-100">{g.name} <span className="text-xs text-gray-400">({(g.student_ids || []).length})</span></span>
                                    <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 h-8 w-8 p-0" onClick={() => deleteGroup(g.id)} data-testid={`poe-delete-group-${g.id}`}><Trash2 className="w-4 h-4" /></Button>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">Assign students to groups from the Students tab.</p>
                    </DialogContent>
                </Dialog>

                <SignatureModal open={sigModal.open} onOpenChange={(o) => setSigModal(s => ({ ...s, open: o }))} title={sigModal.title} onSave={(d) => sigModal.handler && sigModal.handler(d)} />
            </div>
        </Layout>
    );
};

export default POEUnitDetailPage;
